import { DescriptionList } from '@circlefin/components'
import { useDate, useMoney } from '@circlefin/hooks'
import { usePermission } from '@circlefin/permissions/react'
import { BURN_FEE_IMPROVEMENTS } from '@services/permissions'
import useTranslation from 'next-translate/useTranslation'

import { DailyFeesTable } from '../../../containers/DailyFeesTable/DailyFeesTable'

import type { GrossRedemptionFeePayment } from '@shared/graphql'

export interface GrossRedemptionFeeCardProps {
  /**
   * Gross Redemption Fee Payment Object.
   */
  payment: GrossRedemptionFeePayment
}

export const GrossRedemptionFeeCard: React.FC<GrossRedemptionFeeCardProps> = ({
  payment,
}) => {
  const { t } = useTranslation('payments')
  const { dateNumeric: formatDate } = useDate()
  const [{ Authorized }] = usePermission(BURN_FEE_IMPROVEMENTS)
  const { money } = useMoney()

  return (
    <div className="flex flex-col gap-2">
      <h2 className="type-h-title-lg">
        {t('redemptionFeeDetail.details.title')}
      </h2>

      <div className="flex flex-col gap-4">
        <DescriptionList density="loose">
          {/* Transaction Type */}
          <DescriptionList.Label className="w-64">
            {t('redemptionFeeDetail.details.type')}
          </DescriptionList.Label>
          <DescriptionList.Description>
            <p className="type-body-base-bold">
              {t('redemptionFeeDetail.details.grossRedemptionFee')}
            </p>
          </DescriptionList.Description>

          {/* Status */}
          <DescriptionList.Label className="w-64">
            {t('redemptionFeeDetail.details.status')}
          </DescriptionList.Label>
          <DescriptionList.Description className="flex flex-col gap-1">
            <p className="type-body-base-bold">
              {t(`redemptionFeeDetail.details.statusLabels.${payment.status}`)}
            </p>
            <p className="type-body-xs">{formatDate(payment.updateDate)}</p>
          </DescriptionList.Description>

          {/* Amount */}
          <DescriptionList.Label className="w-64">
            {t('redemptionFeeDetail.details.amount')}
          </DescriptionList.Label>
          <DescriptionList.Description>
            <p className="type-body-base-bold">
              {money({
                number: payment.amount.amount,
                variant: payment.amount.currency,
                options: {
                  symbol: true,
                  signDisplay: false,
                },
              })}
            </p>
          </DescriptionList.Description>

          {/* Payout Id */}
          <DescriptionList.Label className="w-64">
            {t('redemptionFeeDetail.details.payoutId')}
          </DescriptionList.Label>
          <DescriptionList.Description>
            <p className="type-body-base-bold">
              {payment.relatedTransactionId}
            </p>
          </DescriptionList.Description>

          {/* Wallet Id */}
          <DescriptionList.Label className="w-64">
            {t('redemptionFeeDetail.details.walletId')}
          </DescriptionList.Label>
          <DescriptionList.Description>
            <p className="type-body-base-bold">{payment.location.id}</p>
          </DescriptionList.Description>
        </DescriptionList>

        {/* Daily Fees Table */}
        <Authorized>
          <DailyFeesTable payment={payment} />
        </Authorized>
      </div>
    </div>
  )
}
